import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { AdminApi } from '../api/admin';
import { asFailure } from '../api/errors';
import type { ApiFailure } from '../api/errors';
import type { AuthProvider, OperatorUser } from '../auth/provider';
import type { ConsoleConfig } from '../config';

/**
 * The binding between the API client and React, in about a hundred lines.
 *
 * `apps/app` has `useLive`, which re-runs a **local** read whenever the mirror
 * bumps a revision, and therefore never has a loading state to invent. This
 * console has no mirror and the difference shapes everything: every screen here
 * is a network read, so a read has three honest outcomes — not yet, this, or
 * the server said no — and `useRead` returns all three rather than collapsing
 * "failed" into "empty".
 *
 * **Nothing is optimistic.** An action is a request; its result is the truth.
 * A view calls the API, and on success calls `reload()` — so what is on screen
 * after a click is what the server said, not what the console hoped. That is
 * not a performance compromise; on a cross-tenant surface it is the difference
 * between "this tenant is live" and "somebody clicked live".
 */

export interface Console {
  api: AdminApi;
  auth: AuthProvider;
  config: ConsoleConfig;
}

const ConsoleContext = createContext<Console | null>(null);

export function ConsoleProvider({
  console: value,
  children,
}: {
  console: Console;
  children: ReactNode;
}): ReactNode {
  return <ConsoleContext.Provider value={value}>{children}</ConsoleContext.Provider>;
}

export function useConsole(): Console {
  const value = useContext(ConsoleContext);
  if (value === null) throw new Error('useConsole outside a ConsoleProvider');
  return value;
}

export function useApi(): AdminApi {
  return useConsole().api;
}

/** Who is signed in. Null until the provider's first `onChange`. */
export function useOperator(): OperatorUser | null {
  const { auth } = useConsole();
  const [user, setUser] = useState<OperatorUser | null>(() => auth.current());
  useEffect(() => auth.onChange(setUser), [auth]);
  return user;
}

export interface ReadState<T> {
  /** The last successful answer, kept while a reload is in flight. */
  value: T | null;
  /** Non-null when the last attempt failed. Rendered in the server's words. */
  failure: ApiFailure | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Run a read, and again whenever `deps` change or `reload()` is called.
 *
 * The previous value is **kept** while a reload runs. An operator watching a
 * conformance execution poll every few seconds should not see the table blink
 * to empty and back; and after a failed reload the screen still shows the last
 * thing the server actually said, with the failure beside it rather than
 * instead of it.
 *
 * A superseded request never writes its answer: `seq` is checked on the way
 * out, so a slow tenant read cannot land after the operator has already clicked
 * into another tenant.
 */
export function useRead<T>(read: () => Promise<T>, deps: readonly unknown[]): ReadState<T> {
  const [value, setValue] = useState<T | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const readRef = useRef(read);
  readRef.current = read;
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    setLoading(true);
    readRef
      .current()
      .then((answer) => {
        if (seq.current !== mine) return;
        setValue(answer);
        setFailure(null);
      })
      .catch((e: unknown) => {
        if (seq.current !== mine) return;
        setFailure(asFailure(e));
      })
      .finally(() => {
        if (seq.current === mine) setLoading(false);
      });
    // `read` is held in a ref so an inline closure does not re-fire the effect:
    // every screen here passes one, and a fresh function identity per render
    // would make the effect fire forever. The caller's `deps` are the whole of
    // the dependency contract, which is why they are an explicit argument.
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { value, failure, loading, reload };
}

export interface ActionState {
  /** The failure of the last attempt, or null. Shown where the button is. */
  failure: ApiFailure | null;
  busy: boolean;
  /** Run it. Resolves to true when it succeeded, so callers can advance. */
  run: (work: () => Promise<void>) => Promise<boolean>;
  clear: () => void;
}

/**
 * One button's worth of state: in flight, and what the server said if it
 * refused.
 *
 * Failed is **shown failed** and stays on the screen until the next attempt.
 * There is no toast: a toast is a refusal an operator can miss, and half the
 * refusals on this surface — "two keys are already active", "products still
 * depends on sales" — are the ones that carry the instruction for what to do
 * instead.
 */
export function useAction(): ActionState {
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const run = useCallback(async (work: () => Promise<void>): Promise<boolean> => {
    setBusy(true);
    setFailure(null);
    try {
      await work();
      return true;
    } catch (e) {
      if (alive.current) setFailure(asFailure(e));
      return false;
    } finally {
      if (alive.current) setBusy(false);
    }
  }, []);

  const clear = useCallback(() => setFailure(null), []);
  return { failure, busy, run, clear };
}
